import { LucideIcon } from "lucide-react";
import { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface PageHeaderFrameProps {
  /** Icon rendered both as a big faded watermark and inside the icon chip next to the title. */
  icon: LucideIcon;
  title: string;
  /** Summary line under the title. Accepts JSX so pages can bold inline stats. */
  description?: ReactNode;
  /** Right slot — action button or a short badge (e.g. "Week 33"). */
  right?: ReactNode;
  /** Extra rows rendered inside the frame below the title/description row, e.g. attention pills. */
  children?: ReactNode;
  /** Escape hatch for spacing overrides (e.g. a page-specific bottom margin). */
  className?: string;
}

/**
 * Shared page-identity block. Every class here is copied verbatim from the OKRs header — that page
 * is the reference; pages using this frame differ from it ONLY in content. Change sizing/spacing
 * here and every page header moves together; never fork a per-page variant.
 */
export const PageHeaderFrame = ({ icon: Icon, title, description, right, children, className }: PageHeaderFrameProps) => (
  <div
    className={cn(
      "relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-background to-secondary/5 border border-primary/10 p-8 mb-6 shadow-sm",
      className,
    )}
  >
    <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
      <Icon className="w-64 h-64 -mt-16 -mr-16" />
    </div>

    <div className="relative z-10 flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 rounded-xl bg-primary/20 text-primary">
              <Icon className="h-6 w-6" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
          </div>
          {description != null && <p className="text-muted-foreground max-w-xl">{description}</p>}
        </div>

        {right != null && <div className="shrink-0">{right}</div>}
      </div>

      {children}
    </div>
  </div>
);
